import { ReactNode } from "react";
import type { CloseButtonProps, AnchorProps } from "../Button";

export enum ToastVariant {
  information = "information",
  warning = "warning",
  success = "success",
  error = "error"
}

export type ToastIconProps = {
  /**
   * One of the available variants, will handle the kind of icon rendered.
   * @default "information"
   */
  variant?: keyof typeof ToastVariant;
};

export type ToastCloseProps = CloseButtonProps & {
  /**
   * Optional additional class name
   */
  className?: string;
};

export type ToastContentProps = {
  /**
   * Content of the toast, usually Title, Description, and Link
   */
  children: ReactNode;
  /**
   * Optional class name for styling
   */
  className?: string;
};

export type ToastTitleProps = {
  /**
   * Content of the title
   */
  children: ReactNode;
  /**
   * Optional class name for styling
   */
  className?: string;
};

export type ToastDescriptionProps = {
  /**
   * Content of the description
   */
  children: ReactNode;
  /**
   * Optional class name for styling
   */
  className?: string;
};

export type ToastViewportProps = {
  /**
   * Optional class name for styling
   */
  className?: string;
  /**
   * The time in milliseconds that should elapse before automatically closing each toast.
   */
  duration?: number;
  /**
   * An author-localized label for each toast. Used to help screen reader users
   * associate the interruption with a toast.
   */
  label?: string;
  /**
   * An array of hotkey character combinations that will close the toast.
   */
  hotkey?: string[];
};

export type ToastAnchorLinkProps = {
  /**
   * Content of the link
   */
  children: ReactNode;
  /**
   * Optional class name for styling
   */
  className?: string;
  /**
   * URL for external/anchor links
   */
  href: string;
  /**
   * Target for the link
   */
  target?: string;
  /**
   * Rel attribute for the link
   */
  rel?: string;
  /**
   * Analytics ID for tracking
   */
  analyticId?: string;
};

export type ToastRouterLinkProps = {
  /**
   * Content of the link
   */
  children: ReactNode;
  /**
   * Optional class name for styling
   */
  className?: string;
  /**
   * Route path for internal router links
   */
  to: string;
  /**
   * Analytics ID for tracking
   */
  analyticId?: string;
};

// Union type for the component to accept either
export type ToastLinkProps = ToastAnchorLinkProps | ToastRouterLinkProps;
